import {
  BattleCardBreakdown,
  BattleCardView,
  BattleOutcome,
  BattlePlayerInfo,
  ColorRank,
  DeckBattleCancelledPayload,
  DeckBattleMode,
  DeckBattleResumePayload,
  DeckBattleStartedPayload,
  DeckChallengeReceivedPayload,
  DeckChallengeSentPayload,
  DeckMatchResultPayload,
  DeckOpponentCardReadyPayload,
  DeckOpponentDisconnectedPayload,
  DeckOpponentReconnectedPayload,
  DeckPhase,
  DeckPlayCardWaitingPayload,
  DeckRadarStatus,
  DeckRoundResultPayload,
  DeckRoundSummary,
  DeckSearchMode,
} from "../types/battle"
import { calcBaseBS } from "../constants/game.constants"
import { normalizeColorRank } from "./radarStatus"
import { normalizeBattlePlayerInfo } from "./challengePayload"
import { normalizeDeckSearchMode } from "./deckSearch"
import { normalizeBattleCardView } from "./battleCards"

function pickNumber(value: unknown, fallback = 0): number {
  if (typeof value === "number" && Number.isFinite(value)) return value
  if (typeof value === "string" && value.trim()) {
    const n = Number(value)
    return Number.isFinite(n) ? n : fallback
  }
  return fallback
}

function pickString(value: unknown, fallback = ""): string {
  if (typeof value === "string") return value
  if (value != null) return String(value)
  return fallback
}

function pickBool(value: unknown, fallback = false): boolean {
  if (typeof value === "boolean") return value
  if (value === "true" || value === 1) return true
  if (value === "false" || value === 0) return false
  return fallback
}

function normalizeBattleOutcome(value: unknown): BattleOutcome {
  const raw = pickString(value).toUpperCase()
  if (raw === "WIN" || raw === "LOSE" || raw === "DRAW") return raw
  return "DRAW"
}

function normalizeDeckMode(value: unknown): DeckBattleMode {
  const raw = pickString(value).toUpperCase()
  if (raw === "DECK_CHALLENGE") return "DECK_CHALLENGE"
  return "DECK_RADAR"
}

function normalizeDeckPhase(value: unknown): DeckPhase {
  const raw = pickString(value).toUpperCase()
  if (raw === "MATCH_END") return "MATCH_END"
  return "PLAY_CARDS"
}

function normalizeBattleCardBreakdown(raw: unknown): BattleCardBreakdown {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return {
      baseBS: 0,
      schoolCoefficient: 1,
      rebornMultiplier: 1,
      elementBonus: 0,
      styleBonus: 0,
      shardBonus: 0,
      guardianPassiveBonus: 0,
      guardianPassivePhase: null,
      guardianPassiveLabel: null,
      petBonus: 0,
      petLabel: null,
      petLevel: null,
      petCritTriggered: false,
      petDodgeTriggered: false,
      guardianCritTriggered: false,
      guardianDodgeTriggered: false,
      shardCritTriggered: false,
      shardDodgeTriggered: false,
      shardPtsLossReduction: 0,
      iceAntiElementPenalty: 0,
      abilityId: null,
      abilitySlot: null,
      abilityTitle: null,
      abilityBonus: 0,
      abilityDebuffDealt: 0,
      abilitySilenced: false,
      effectiveBS: 0,
    }
  }
  const obj = raw as Record<string, unknown>
  const baseBS = pickNumber(obj.baseBS ?? obj.base_bs)
  const effectiveBS = pickNumber(obj.effectiveBS ?? obj.effective_bs, baseBS)
  return {
    baseBS,
    schoolCoefficient: pickNumber(obj.schoolCoefficient ?? obj.school_coefficient, 1),
    rebornMultiplier: pickNumber(obj.rebornMultiplier ?? obj.reborn_multiplier, 1),
    elementBonus: pickNumber(obj.elementBonus ?? obj.element_bonus),
    styleBonus: pickNumber(obj.styleBonus ?? obj.style_bonus),
    shardBonus: pickNumber(obj.shardBonus ?? obj.shard_bonus),
    guardianPassiveBonus: pickNumber(
      obj.guardianPassiveBonus ?? obj.guardian_passive_bonus
    ),
    guardianPassivePhase:
      pickString(obj.guardianPassivePhase ?? obj.guardian_passive_phase) === "late"
        ? "late"
        : pickString(obj.guardianPassivePhase ?? obj.guardian_passive_phase) === "early"
          ? "early"
          : null,
    guardianPassiveLabel:
      pickString(obj.guardianPassiveLabel ?? obj.guardian_passive_label) || null,
    petBonus: pickNumber(obj.petBonus ?? obj.pet_bonus),
    petLabel: pickString(obj.petLabel ?? obj.pet_label) || null,
    petLevel: (() => {
      const raw = obj.petLevel ?? obj.pet_level
      if (raw == null || raw === "") return null
      const n = Math.floor(pickNumber(raw, 0))
      return n > 0 ? n : null
    })(),
    petCritTriggered: pickBool(
      obj.petCritTriggered ?? obj.pet_crit_triggered
    ),
    petDodgeTriggered: pickBool(
      obj.petDodgeTriggered ?? obj.pet_dodge_triggered
    ),
    guardianCritTriggered: pickBool(
      obj.guardianCritTriggered ?? obj.guardian_crit_triggered
    ),
    guardianDodgeTriggered: pickBool(
      obj.guardianDodgeTriggered ?? obj.guardian_dodge_triggered
    ),
    shardCritTriggered: pickBool(
      obj.shardCritTriggered ?? obj.shard_crit_triggered
    ),
    shardDodgeTriggered: pickBool(
      obj.shardDodgeTriggered ?? obj.shard_dodge_triggered
    ),
    shardPtsLossReduction: pickNumber(
      obj.shardPtsLossReduction ?? obj.shard_pts_loss_reduction
    ),
    iceAntiElementPenalty: pickNumber(
      obj.iceAntiElementPenalty ??
        obj.ice_anti_element_penalty ??
        obj.iceAntiStylePenalty ??
        obj.ice_anti_style_penalty
    ),
    abilityId: pickString(obj.abilityId ?? obj.ability_id) || null,
    abilitySlot: (() => {
      const slot = pickNumber(obj.abilitySlot ?? obj.ability_slot, 0)
      return slot === 1 || slot === 2 || slot === 3 ? slot : null
    })(),
    abilityTitle: pickString(obj.abilityTitle ?? obj.ability_title) || null,
    abilityBonus: pickNumber(obj.abilityBonus ?? obj.ability_bonus),
    abilityDebuffDealt: pickNumber(
      obj.abilityDebuffDealt ?? obj.ability_debuff_dealt
    ),
    abilitySilenced: pickBool(obj.abilitySilenced ?? obj.ability_silenced),
    effectiveBS,
  }
}

function normalizeColorRankList(value: unknown): ColorRank[] {
  if (!Array.isArray(value)) return []
  return value
    .map((item) => normalizeColorRank(item))
    .filter((rank): rank is ColorRank => rank != null)
}

export function normalizeDeckRadarStatus(raw: unknown): DeckRadarStatus | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>
  const deckSearchMode = normalizeDeckSearchMode(
    obj.deckSearchMode ?? obj.deck_search_mode
  )
  return {
    queueSize: pickNumber(obj.queueSize ?? obj.queue_size),
    matchableCount: pickNumber(obj.matchableCount ?? obj.matchable_count),
    availableRanks: normalizeColorRankList(obj.availableRanks ?? obj.available_ranks),
    deckSize: pickNumber(obj.deckSize ?? obj.deck_size, 6),
    roundsToWin: pickNumber(obj.roundsToWin ?? obj.rounds_to_win, 3),
    energyCost: pickNumber(obj.energyCost ?? obj.energy_cost, 15),
    deckSearchMode,
    canBuildAnyDeck:
      obj.canBuildAnyDeck != null || obj.can_build_any_deck != null
        ? pickBool(obj.canBuildAnyDeck ?? obj.can_build_any_deck)
        : undefined,
    totalCards: pickNumber(obj.totalCards ?? obj.total_cards) || undefined,
    colorRank: normalizeColorRank(obj.colorRank ?? obj.color_rank) ?? undefined,
    message: pickString(obj.message) || undefined,
  }
}

export function normalizeDeckBattleStartedPayload(
  raw: unknown
): DeckBattleStartedPayload | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>
  const battleId = pickString(obj.battleId ?? obj.battle_id)
  if (!battleId) return null

  const you = normalizeBattlePlayerInfo(obj.you)
  const opponent = normalizeBattlePlayerInfo(obj.opponent)
  if (!you || !opponent) return null

  const deckRaw = obj.yourDeck ?? obj.your_deck ?? obj.yourCards ?? obj.your_cards
  const yourDeck = Array.isArray(deckRaw)
    ? deckRaw
        .map(normalizeBattleCardView)
        .filter((card): card is BattleCardView => card != null)
    : []

  const deckSearchMode = normalizeDeckSearchMode(
    obj.deckSearchMode ?? obj.deck_search_mode
  )
  const colorRank = normalizeColorRank(obj.colorRank ?? obj.color_rank)

  const phase = normalizeDeckPhase(obj.phase)
  const firstPlayerRaw = obj.firstPlayerUserId ?? obj.first_player_user_id
  const firstPlayerUserId =
    firstPlayerRaw == null || firstPlayerRaw === ""
      ? undefined
      : pickNumber(firstPlayerRaw, NaN)
  const normalizedFirstPlayer =
    firstPlayerUserId != null && Number.isFinite(firstPlayerUserId)
      ? firstPlayerUserId
      : undefined

  return {
    battleId,
    mode: normalizeDeckMode(obj.mode),
    deckSearchMode,
    colorRank: deckSearchMode === "RANK" ? colorRank ?? "WHITE" : colorRank ?? undefined,
    you,
    opponent,
    yourDeck,
    phase: obj.phase != null ? phase : undefined,
    currentRound:
      obj.currentRound != null || obj.current_round != null
        ? pickNumber(obj.currentRound ?? obj.current_round, 1)
        : undefined,
    yourRoundWins:
      obj.yourRoundWins != null || obj.your_round_wins != null
        ? pickNumber(obj.yourRoundWins ?? obj.your_round_wins)
        : undefined,
    opponentRoundWins:
      obj.opponentRoundWins != null || obj.opponent_round_wins != null
        ? pickNumber(obj.opponentRoundWins ?? obj.opponent_round_wins)
        : undefined,
    firstPlayerUserId: normalizedFirstPlayer,
    roundsToWin: pickNumber(obj.roundsToWin ?? obj.rounds_to_win, 3),
    playCardTimeoutSec: pickNumber(
      obj.playCardTimeoutSec ?? obj.play_card_timeout_sec,
      45
    ),
  }
}

export function normalizeDeckChallengeReceivedPayload(
  raw: unknown
): DeckChallengeReceivedPayload | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>
  const challengeId = pickString(obj.challengeId ?? obj.challenge_id)
  if (!challengeId) return null

  const deckSearchMode = normalizeDeckSearchMode(
    obj.deckSearchMode ?? obj.deck_search_mode
  )
  const colorRank = normalizeColorRank(obj.colorRank ?? obj.color_rank)

  return {
    challengeId,
    fromUserId: pickNumber(obj.fromUserId ?? obj.from_user_id),
    fromNickname: pickString(obj.fromNickname ?? obj.from_nickname, "Хранитель"),
    deckSearchMode,
    colorRank: deckSearchMode === "RANK" ? colorRank ?? "WHITE" : colorRank ?? undefined,
    deckSize: pickNumber(obj.deckSize ?? obj.deck_size, 6),
    timeoutSec: pickNumber(obj.timeoutSec ?? obj.timeout_sec, 60),
    expiresInSec: pickNumber(obj.expiresInSec ?? obj.expires_in_sec) || undefined,
  }
}

export function normalizeDeckChallengeSentPayload(
  raw: unknown
): DeckChallengeSentPayload | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>
  const challengeId = pickString(obj.challengeId ?? obj.challenge_id)
  if (!challengeId) return null

  const deckSearchMode = normalizeDeckSearchMode(
    obj.deckSearchMode ?? obj.deck_search_mode
  )
  const colorRank = normalizeColorRank(obj.colorRank ?? obj.color_rank)

  return {
    challengeId,
    targetUserId: pickNumber(obj.targetUserId ?? obj.target_user_id),
    deckSearchMode,
    colorRank: deckSearchMode === "RANK" ? colorRank ?? "WHITE" : colorRank ?? undefined,
    expiresInSec: pickNumber(obj.expiresInSec ?? obj.expires_in_sec, 60),
  }
}

export function normalizeDeckPlayCardWaitingPayload(
  raw: unknown
): DeckPlayCardWaitingPayload | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>
  const battleId = pickString(obj.battleId ?? obj.battle_id)
  if (!battleId) return null
  return { battleId }
}

export function normalizeDeckOpponentCardReadyPayload(
  raw: unknown
): DeckOpponentCardReadyPayload | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>
  const battleId = pickString(obj.battleId ?? obj.battle_id)
  if (!battleId) return null
  return {
    battleId,
    round: pickNumber(obj.round, 1),
  }
}

export function normalizeDeckRoundResultPayload(
  raw: unknown
): DeckRoundResultPayload | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>
  const battleId = pickString(obj.battleId ?? obj.battle_id)
  const firstCard = normalizeBattleCardView(obj.firstCard ?? obj.first_card)
  const secondCard = normalizeBattleCardView(obj.secondCard ?? obj.second_card)
  if (!battleId || !firstCard || !secondCard) return null

  const roundWinnerRaw = obj.roundWinnerUserId ?? obj.round_winner_user_id
  const roundWinnerUserId =
    roundWinnerRaw == null || roundWinnerRaw === ""
      ? null
      : pickNumber(roundWinnerRaw, NaN)
  const winner =
    roundWinnerUserId != null && Number.isFinite(roundWinnerUserId)
      ? roundWinnerUserId
      : null

  const firstPlayerRaw = obj.firstPlayerUserId ?? obj.first_player_user_id
  const firstPlayerParsed =
    firstPlayerRaw == null || firstPlayerRaw === ""
      ? NaN
      : pickNumber(firstPlayerRaw, NaN)
  const firstPlayerUserId = Number.isFinite(firstPlayerParsed)
    ? firstPlayerParsed
    : 0

  return {
    battleId,
    round: pickNumber(obj.round, 1),
    roundWinnerUserId: winner,
    roundOutcome: normalizeBattleOutcome(obj.roundOutcome ?? obj.round_outcome),
    firstCard,
    secondCard,
    firstCombat: normalizeBattleCardBreakdown(obj.firstCombat ?? obj.first_combat),
    secondCombat: normalizeBattleCardBreakdown(obj.secondCombat ?? obj.second_combat),
    yourRoundWins: pickNumber(obj.yourRoundWins ?? obj.your_round_wins),
    opponentRoundWins: pickNumber(obj.opponentRoundWins ?? obj.opponent_round_wins),
    yourRemainingCards: pickNumber(
      obj.yourRemainingCards ?? obj.your_remaining_cards
    ),
    opponentRemainingCards: pickNumber(
      obj.opponentRemainingCards ?? obj.opponent_remaining_cards
    ),
    phase: normalizeDeckPhase(obj.phase),
    currentRound: pickNumber(obj.currentRound ?? obj.current_round, 1),
    firstPlayerUserId,
  }
}

function normalizeDeckRoundSummary(raw: unknown): DeckRoundSummary | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>
  const firstCard = normalizeBattleCardView(obj.firstCard ?? obj.first_card)
  const secondCard = normalizeBattleCardView(obj.secondCard ?? obj.second_card)
  if (!firstCard || !secondCard) return null

  const roundWinnerRaw = obj.roundWinnerUserId ?? obj.round_winner_user_id
  const roundWinnerUserId =
    roundWinnerRaw == null || roundWinnerRaw === ""
      ? null
      : pickNumber(roundWinnerRaw, NaN)

  return {
    round: pickNumber(obj.round, 1),
    roundOutcome: normalizeBattleOutcome(obj.roundOutcome ?? obj.round_outcome),
    roundWinnerUserId:
      roundWinnerUserId != null && Number.isFinite(roundWinnerUserId)
        ? roundWinnerUserId
        : null,
    firstCard,
    secondCard,
    firstCombat: normalizeBattleCardBreakdown(obj.firstCombat ?? obj.first_combat),
    secondCombat: normalizeBattleCardBreakdown(obj.secondCombat ?? obj.second_combat),
    firstPlayerUserId: pickNumber(obj.firstPlayerUserId ?? obj.first_player_user_id),
  }
}

export function normalizeDeckMatchResultPayload(
  raw: unknown
): DeckMatchResultPayload | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>
  const battleId = pickString(obj.battleId ?? obj.battle_id)
  if (!battleId) return null

  const you = normalizeBattlePlayerInfo(obj.you)
  const opponent = normalizeBattlePlayerInfo(obj.opponent)
  if (!you || !opponent) return null

  const roundsRaw = Array.isArray(obj.rounds) ? obj.rounds : []
  const rounds = roundsRaw
    .map(normalizeDeckRoundSummary)
    .filter((round): round is DeckRoundSummary => round != null)

  return {
    battleId,
    mode: normalizeDeckMode(obj.mode),
    deckSearchMode: normalizeDeckSearchMode(
      obj.deckSearchMode ?? obj.deck_search_mode
    ),
    colorRank:
      normalizeDeckSearchMode(obj.deckSearchMode ?? obj.deck_search_mode) === "RANK"
        ? normalizeColorRank(obj.colorRank ?? obj.color_rank) ?? "WHITE"
        : normalizeColorRank(obj.colorRank ?? obj.color_rank) ?? undefined,
    matchScore: pickString(obj.matchScore ?? obj.match_score, "0:0"),
    outcome: normalizeBattleOutcome(obj.outcome),
    ptsDelta: pickNumber(obj.ptsDelta ?? obj.pts_delta),
    coinsDelta: pickNumber(obj.coinsDelta ?? obj.coins_delta),
    rounds,
    you,
    opponent,
    yourRoundWins: pickNumber(obj.yourRoundWins ?? obj.your_round_wins),
    opponentRoundWins: pickNumber(obj.opponentRoundWins ?? obj.opponent_round_wins),
  }
}

export function normalizeDeckBattleCancelledPayload(
  raw: unknown
): DeckBattleCancelledPayload | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>
  const battleId = pickString(obj.battleId ?? obj.battle_id)
  if (!battleId) return null

  const reasonRaw = pickString(obj.reason).toUpperCase()
  let reason: DeckBattleCancelledPayload["reason"]
  if (reasonRaw === "PLAY_CARD_TIMEOUT") {
    reason = "PLAY_CARD_TIMEOUT"
  } else if (reasonRaw === "PHASE_TIMEOUT") {
    reason = "PHASE_TIMEOUT"
  } else if (reasonRaw === "OPPONENT_DISCONNECTED") {
    reason = "OPPONENT_DISCONNECTED"
  } else {
    reason = reasonRaw || "OPPONENT_DISCONNECTED"
  }

  return {
    battleId,
    mode: normalizeDeckMode(obj.mode),
    reason,
    message: pickString(obj.message) || undefined,
    energyRefunded: pickBool(obj.energyRefunded ?? obj.energy_refunded, true),
  }
}

export function normalizeDeckBattleResumePayload(
  raw: unknown
): DeckBattleResumePayload | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>
  const battleId = pickString(obj.battleId ?? obj.battle_id)
  if (!battleId) return null

  const you = normalizeBattlePlayerInfo(obj.you)
  const opponent = normalizeBattlePlayerInfo(obj.opponent)
  if (!you || !opponent) return null

  const deckRaw = obj.yourDeck ?? obj.your_deck ?? obj.yourCards ?? obj.your_cards
  const yourDeck = Array.isArray(deckRaw)
    ? deckRaw
        .map(normalizeBattleCardView)
        .filter((card): card is BattleCardView => card != null)
    : []

  const remainingRaw = obj.yourRemainingDeck ?? obj.your_remaining_deck
  const yourRemainingDeck = Array.isArray(remainingRaw)
    ? remainingRaw
        .map(normalizeBattleCardView)
        .filter((card): card is BattleCardView => card != null)
    : yourDeck

  const deckSearchMode = normalizeDeckSearchMode(
    obj.deckSearchMode ?? obj.deck_search_mode
  )
  const colorRank = normalizeColorRank(obj.colorRank ?? obj.color_rank)

  const pendingRaw = obj.yourPendingCardId ?? obj.your_pending_card_id
  const yourPendingCardId =
    pendingRaw == null || pendingRaw === ""
      ? null
      : pickString(pendingRaw) || null

  const roundsRaw = Array.isArray(obj.rounds) ? obj.rounds : []
  const rounds = roundsRaw
    .map(normalizeDeckRoundSummary)
    .filter((round): round is DeckRoundSummary => round != null)

  const playTimeout = pickNumber(
    obj.playCardTimeoutSec ?? obj.play_card_timeout_sec,
    45
  )
  const secondsLeftRaw = obj.secondsLeft ?? obj.seconds_left
  const secondsLeft =
    secondsLeftRaw != null
      ? Math.max(0, pickNumber(secondsLeftRaw, playTimeout))
      : playTimeout

  return {
    battleId,
    mode: normalizeDeckMode(obj.mode),
    deckSearchMode,
    colorRank:
      deckSearchMode === "RANK" ? colorRank ?? "WHITE" : colorRank ?? undefined,
    you,
    opponent,
    yourDeck,
    yourRemainingDeck,
    yourPendingCardId,
    opponentCardReady: pickBool(
      obj.opponentCardReady ?? obj.opponent_card_ready
    ),
    opponentDisconnected: pickBool(
      obj.opponentDisconnected ?? obj.opponent_disconnected
    ),
    phase: normalizeDeckPhase(obj.phase),
    currentRound: pickNumber(obj.currentRound ?? obj.current_round, 1),
    yourRoundWins: pickNumber(obj.yourRoundWins ?? obj.your_round_wins),
    opponentRoundWins: pickNumber(
      obj.opponentRoundWins ?? obj.opponent_round_wins
    ),
    roundsToWin: pickNumber(obj.roundsToWin ?? obj.rounds_to_win, 3),
    playCardTimeoutSec: playTimeout,
    secondsLeft,
    reconnectGraceSec: pickNumber(
      obj.reconnectGraceSec ?? obj.reconnect_grace_sec,
      60
    ),
    rounds,
  }
}

export function normalizeDeckOpponentDisconnectedPayload(
  raw: unknown
): DeckOpponentDisconnectedPayload | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>
  const battleId = pickString(obj.battleId ?? obj.battle_id)
  if (!battleId) return null
  return {
    battleId,
    graceSec: pickNumber(obj.graceSec ?? obj.grace_sec, 60),
  }
}

export function normalizeDeckOpponentReconnectedPayload(
  raw: unknown
): DeckOpponentReconnectedPayload | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>
  const battleId = pickString(obj.battleId ?? obj.battle_id)
  if (!battleId) return null
  return { battleId }
}

export function normalizeDeckChallengeStatusPayload(
  raw: unknown
): { challengeId: string; message: string } | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>
  const challengeId = pickString(obj.challengeId ?? obj.challenge_id)
  if (!challengeId) return null
  return {
    challengeId,
    message: pickString(obj.message, "Ошибка вызова"),
  }
}
